import React, {useRef, useState, useEffect, startTransition, Suspense} from "react"
import UxToolTip from "@next/ux-tooltip"
import {
    useShoppingBagGetCallbackObservable,
    useShoppingBagAddCallbackObservable,
    useShoppingBagAddCistCallbackObservable,
    useShoppingBagAddEVoucherCallbackObservable,
    useShoppingBagAddLinkedItemCallbackObservable,
    useShoppingBagAddMultipleCallbackObservable,
    useShoppingBagAddWarrantyCallbackObservable,
    useShoppingBagRemoveCallbackObservable,
    useShoppingBagUpdateSizeCallbackObservable,
    useShoppingBagUpdateQuantityCallbackObservable,
    useModalsCloseObservable,
    useShoppingBagAcceptCallbackObservable,
    useShoppingBagRejectCallbackObservable,
    useShoppingBagAddSampleCallbackObservable,
    useShoppingBagRemoveSampleCallbackObservable,
    useShoppingBagAddGiftCardCallbackObservable,
    useShoppingBagAddCustomItemCallbackObservable,
    useShoppingBagAddPersonalisedCallbackObservable,
} from "@next/eventservice"
import {PublishToModalsClosed} from "../../events/modalsClosed"
import connect from "./connect"
import {ToolTipContent, Container, StyledBox} from "./components"
import ShoppingBagNotification from "../ShoppingBagNotification"
import {removeDeptFromSessionStorage} from "../../utils/removeDeptFromSessionStorage"
import ShoppingBagIcon from "../ShoppingBagIcon"
import {useBagFlashNotification} from "../../hooks/NextVisitor/useBagFlashNotification"
import {ShoppingBag as ShoppingBagType} from "../../models/shoppingbag"
import {
    getNextVisitorCookieTimestamp,
    getSessionStorageTimestamp,
} from "../../hooks/NextVisitor/useBagFlashNotification.utils"

export type ShoppingBagProps = {
    miniBagTooltipPlacement: any
    altText: string
    smallIconUrl: string
    largeIconUrl: string
    itemCount: number
    updateShoppingBag: (data: any) => void
    addEVoucherToBag: (data: any) => void
    isBagLoading: boolean
    shoppingBagUrl: string
    tooltipModifiers?: Parameters<typeof UxToolTip>[0]["modifiers"]
    applyMarginCompensation?: boolean
    enableMinibagNotification: boolean
    isSecure?: boolean
}
const MiniShoppingBag = React.lazy(() => import("../MiniShoppingBag"))

export const ShoppingBag = ({
    miniBagTooltipPlacement = "bottom",
    altText,
    smallIconUrl,
    largeIconUrl,
    itemCount,
    updateShoppingBag,
    addEVoucherToBag,
    isBagLoading,
    shoppingBagUrl,
    tooltipModifiers,
    enableMinibagNotification,
    applyMarginCompensation = false,
    isSecure = false,
}: ShoppingBagProps) => {
    const [isBagVisible, setIsBagVisible] = useState(false)
    const thisElement = useRef(null)
    const [bag, setBag] = useState<ShoppingBagType | null>(null)
    const openBag = () => {
        if (!isBagVisible) setIsBagVisible(true)
    }

    // Get NextVisitor cookie and session storage timestamps to determine if we should show the bag flash notification
    const nextVisitorCookieTimestamp = getNextVisitorCookieTimestamp()
    const nextVisitorSessionTimestamp = getSessionStorageTimestamp()

    const {shouldShowBagNotification, setShouldShowBagNotification} = useBagFlashNotification(
        bag,
        nextVisitorCookieTimestamp,
        nextVisitorSessionTimestamp,
        enableMinibagNotification,
    )

    useShoppingBagGetCallbackObservable(data => {
        startTransition(() => {
            updateShoppingBag(data)
            setBag(data?.data?.ShoppingBag as ShoppingBagType)
        })
    })
    useShoppingBagAddCallbackObservable(data => {
        updateShoppingBag(data)
        openBag()
    })
    useShoppingBagAddCistCallbackObservable(data => {
        updateShoppingBag(data)
        openBag()
    })
    useShoppingBagAddCustomItemCallbackObservable(data => {
        updateShoppingBag(data)
        openBag()
    })
    useShoppingBagAddEVoucherCallbackObservable(data => {
        addEVoucherToBag(data)
        openBag()
    })
    useShoppingBagAddLinkedItemCallbackObservable(data => {
        updateShoppingBag(data)
        openBag()
    })
    useShoppingBagAddMultipleCallbackObservable(data => {
        updateShoppingBag(data)
        openBag()
    })
    useShoppingBagAddWarrantyCallbackObservable(data => {
        updateShoppingBag(data)
    })
    useShoppingBagRemoveCallbackObservable(data => {
        updateShoppingBag(data)
    })
    useShoppingBagUpdateSizeCallbackObservable(data => {
        updateShoppingBag(data)
    })
    useShoppingBagUpdateQuantityCallbackObservable(data => {
        updateShoppingBag(data)
    })

    useModalsCloseObservable(() => {
        setIsBagVisible(false)
    })

    useShoppingBagAcceptCallbackObservable(data => {
        updateShoppingBag(data)
    })

    useShoppingBagRejectCallbackObservable(data => {
        updateShoppingBag(data)
    })

    useShoppingBagAddSampleCallbackObservable(data => {
        updateShoppingBag(data)
    })

    useShoppingBagRemoveSampleCallbackObservable(data => {
        updateShoppingBag(data)
    })

    useShoppingBagAddGiftCardCallbackObservable(data => {
        addEVoucherToBag(data)
        openBag()
    })
    useShoppingBagAddPersonalisedCallbackObservable(data => {
        updateShoppingBag(data)
        openBag()
    })

    const handleOpen = event => {
        PublishToModalsClosed()
        event.preventDefault()
        setIsBagVisible(true)
        setShouldShowBagNotification(false)
    }

    const handleClose = event => {
        if (event?.type === "scroll" && event?.target !== document) return
        setIsBagVisible(false)
    }

    useEffect(() => {
        window.addEventListener("scroll", handleClose, true)
        return () => {
            window.removeEventListener("scroll", handleClose)
        }
    }, [])

    const items = isBagLoading ? "" : itemCount

    return (
        <Container
            className="shoppingbag"
            data-testid="header-shopping-bag"
            ref={thisElement}
            isBadgeIcon={applyMarginCompensation}
        >
            <StyledBox display={isSecure ? "flex" : {xs: "flex", md: "none"}}>
                <ShoppingBagIcon
                    shoppingBagUrl={shoppingBagUrl}
                    altText={altText}
                    iconUrl={smallIconUrl}
                    items={items}
                    testId="header-shopping-bag-link-el"
                    onClick={removeDeptFromSessionStorage}
                    isBagVisible={isBagVisible}
                />
            </StyledBox>
            <StyledBox display={isSecure ? "none" : {xs: "none", md: "flex"}}>
                <ShoppingBagIcon
                    shoppingBagUrl={shoppingBagUrl}
                    altText={altText}
                    iconUrl={largeIconUrl}
                    items={items}
                    testId="shopping-bag-link-button"
                    onClick={handleOpen}
                    isBagVisible={isBagVisible}
                />

                {thisElement.current && (
                    <Suspense>
                        <UxToolTip
                            open={isBagVisible}
                            ref={thisElement}
                            handleClose={handleClose}
                            timeout={1000000}
                            placement={miniBagTooltipPlacement}
                            animationTimeout={300}
                            enableModArrow
                            showHeader
                            modifiers={tooltipModifiers}
                        >
                            <ToolTipContent>
                                <MiniShoppingBag />
                            </ToolTipContent>
                        </UxToolTip>
                    </Suspense>
                )}
            </StyledBox>

            <UxToolTip
                open={shouldShowBagNotification}
                ref={thisElement}
                handleClose={() => setShouldShowBagNotification(false)}
                timeout={2000}
                placement="bottom"
                modifiers={tooltipModifiers}
                enableModArrow
            >
                <ToolTipContent>
                    <ShoppingBagNotification />
                </ToolTipContent>
            </UxToolTip>
        </Container>
    )
}

export default connect(ShoppingBag) as any
